import { useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { useQueryClient } from "@tanstack/react-query";
import type { EstimateChatMessage, EstimateChatSummary } from "@rebuild/shared/models";
import { ESTIMATE_CHAT_KEYS } from "@/hooks/queries/estimate-chat";
import type { EstimateChatRequestBody, EstimateChatUIMessage } from "./estimateChatStream.types";
import { estimateChatTransport, mergeSavedMessages } from "./estimateChatStream.utils";

interface EstimateChatStream {

  streamingTurnMessages: EstimateChatUIMessage[]; //current turn being streamed
  streamingTurnChatId: string | undefined; //chatid being streamed to
  // One turn streams at a time across all of the estimate's chats.
  isTurnInFlight: boolean;
  turnError: Error | undefined;
  // False when the failed turn would fail the same way again, e.g. the chat's history is too long.
  canRetry: boolean;
  send: (chatId: string, content: string) => void;
  retry: () => void;
}

/**
 * Streams agent turns for one estimate's chats. React Query holds the saved history; useChat holds
 * only the turn in flight. When the server sends the saved turn, it is merged into the React Query
 * cache and useChat is cleared, so the two never show the same turn for long.
 */
export function useEstimateChatStream(estimateId: string): EstimateChatStream {
  const queryClient = useQueryClient();
  // The chat the latest turn was sent to, read inside callbacks that outlive a chat switch.
  const turnChatIdRef = useRef<string | undefined>(undefined);
  const [streamingTurnChatId, setStreamingTurnChatId] = useState<string>();
  const [canRetry, setCanRetry] = useState(true);

  const turnStream = useChat<EstimateChatUIMessage>({
    transport: estimateChatTransport,
    generateId: () => crypto.randomUUID(),
    onData: (part) => {
      if (part.type === "data-turn-error") {
        setCanRetry(part.data.canRetry);
        return;
      }
      const targetChatId = turnChatIdRef.current;
      if (part.type !== "data-saved-messages" || !targetChatId) return;
      queryClient.setQueryData<EstimateChatMessage[]>(ESTIMATE_CHAT_KEYS.messages(estimateId, targetChatId), (cached = []) =>
        mergeSavedMessages(cached, part.data.messages),
      );
    },
    onFinish: ({ isError, isAbort }) => {
      const targetChatId = turnChatIdRef.current;
      // Only a chat's first message creates it, so refetch the list only when it lacks this chat.
      const knownChats = queryClient.getQueryData<EstimateChatSummary[]>(ESTIMATE_CHAT_KEYS.chats(estimateId));
      if (targetChatId && !knownChats?.some((c) => c.chatId === targetChatId)) {
        queryClient.invalidateQueries({ queryKey: ESTIMATE_CHAT_KEYS.chats(estimateId) });
      }
      // A failed turn stays so it can be retried.
      if (isError) return;
      turnStream.setMessages([]);
      // An aborted turn's user message was saved but never reached the cache; refetch so it stays visible.
      if (isAbort && targetChatId) {
        queryClient.invalidateQueries({ queryKey: ESTIMATE_CHAT_KEYS.messages(estimateId, targetChatId) });
      }
    },
  });

  const requestBody = (targetChatId: string): EstimateChatRequestBody => ({ estimateId, chatId: targetChatId });

  const send = (targetChatId: string, content: string) => {
    const failedChatId = turnChatIdRef.current;
    if (turnStream.status === "error" && failedChatId) {
      // Sending instead of retrying abandons the failed turn. Its user message was likely saved, so
      // refetch to show it from history.
      turnStream.setMessages([]);
      turnStream.clearError();
      queryClient.invalidateQueries({ queryKey: ESTIMATE_CHAT_KEYS.messages(estimateId, failedChatId) });
    }
    turnChatIdRef.current = targetChatId;
    setStreamingTurnChatId(targetChatId);
    setCanRetry(true);
    turnStream.sendMessage({ text: content }, { body: requestBody(targetChatId) });
  };

  const retry = () => {
    const failedChatId = turnChatIdRef.current;
    // status flips to "submitted" as soon as regenerate starts, so a second click is ignored.
    if (turnStream.status !== "error" || !failedChatId || !canRetry) return;
    turnStream.regenerate({ body: requestBody(failedChatId) });
  };

  return {
    streamingTurnMessages: turnStream.messages,
    streamingTurnChatId,
    isTurnInFlight: turnStream.status === "submitted" || turnStream.status === "streaming",
    turnError: turnStream.error,
    canRetry,
    send,
    retry,
  };
}
